import React, { useState } from "react";
import { Dropdown } from "antd";
import { EllipsisOutlined } from "@ant-design/icons";
import Organizer from "components/organizer";
import Icons from "components/icons";
import { EntityRemoveButton, Localize, ModalStandard } from "components/service";
import { localizeByKey } from "components/service/localize";
import { usePermissionByObject } from "components/permissions";
import EmailChannelForms from "../forms";
import { emailChannelUseMutation } from "../hooks";
import type { TOrganizer } from "components/organizer/helpers/organizer-types-helper";
import type { TEmailChannel } from "../helpers/email-channel-types-helper";

interface IEmailChannelActionFieldProps {
  organizer: TOrganizer;
  channel: TEmailChannel;
  variables: Record<string, any>;
}

const EmailChannelActionField: React.FC<IEmailChannelActionFieldProps> = ({
  organizer,
  channel,
  variables,
}) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { hasPermission } = usePermissionByObject(Organizer.Const.permissions.UPDATE);
  const {
    _setEmailChannelCreateUpdate,
    loadingMutationEmailChannelCreateUpdate,
  } = emailChannelUseMutation.createUpdate(channel.id, variables);

  const updateChannel = (input: { is_active?: boolean; is_visible?: boolean }) =>
    _setEmailChannelCreateUpdate({
      variables: {
        input: {
          id: channel.id,
          exhibition_type_id: channel.exhibition_type_id,
          is_active: input.is_active ?? channel.is_active === true,
          is_visible: input.is_visible ?? channel.is_visible === true,
        },
      },
    });

  const items = [
    {
      key: "status",
      disabled: !hasPermission || loadingMutationEmailChannelCreateUpdate,
      label: (
        <span className="d-flex align-items-center w-100">
          {channel.is_active ? <Icons.Deactivate /> : <Icons.Activate />}
          <Localize>
            {channel.is_active
              ? "EMAIL_CHANNEL.Action_Deactivate"
              : "EMAIL_CHANNEL.Action_Activate"}
          </Localize>
        </span>
      ),
      onClick: () => updateChannel({ is_active: !channel.is_active }),
    },
    {
      key: "visibility",
      disabled: !hasPermission || loadingMutationEmailChannelCreateUpdate,
      label: (
        <span className="d-flex align-items-center w-100">
          {channel.is_visible ? <Icons.Hidden /> : <Icons.Visible />}
          <Localize>
            {channel.is_visible
              ? "EMAIL_CHANNEL.Action_Hide"
              : "EMAIL_CHANNEL.Action_Show"}
          </Localize>
        </span>
      ),
      onClick: () => updateChannel({ is_visible: !channel.is_visible }),
    },
    {
      key: "edit",
      disabled: !hasPermission,
      label: (
        <span className="d-flex align-items-center w-100">
          <Icons.Edit /> <Localize>MENUS_DROP.Label_Edit</Localize>
        </span>
      ),
      onClick: () => setShowEditModal(true),
    },
    {
      key: "delete",
      disabled: !hasPermission || loadingMutationEmailChannelCreateUpdate,
      label: (
        <span className="d-flex align-items-center w-100">
          <Icons.Delete /> <Localize>MENUS_DROP.Label_Delete</Localize>
        </span>
      ),
      onClick: () => setShowDeleteModal(true),
    },
  ];

  return (
    <>
      <Dropdown
        placement="bottomRight"
        menu={{ items }}
        overlayClassName="drop-more-menu"
        arrow
      >
        <EllipsisOutlined className="btn-more" />
      </Dropdown>

      <ModalStandard
        width={500}
        extraClass="modal-form"
        showModal={showEditModal}
        setShowModal={setShowEditModal}
        modalTitle={
          <strong className="modal-title">
            <span className="text">
              <Localize>EMAIL_CHANNEL.Modal_Title_Edit</Localize>
            </span>
          </strong>
        }
      >
        <EmailChannelForms.Edit
          organizer={organizer}
          channel={channel}
          variables={variables}
          action={() => setShowEditModal(false)}
        />
      </ModalStandard>

      <EntityRemoveButton
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
        nameEntity={localizeByKey("EMAIL_CHANNEL.Entity_Name")}
        dataNameEntity={channel.username}
        loading={loadingMutationEmailChannelCreateUpdate}
        disabled={!hasPermission}
        deleteMutation={async (options) => {
          await _setEmailChannelCreateUpdate(options);
        }}
        variables={{
          input: {
            id: channel.id,
            exhibition_type_id: null,
          },
        }}
      />
    </>
  );
};

export default EmailChannelActionField;
